import Typography from '@mui/material/Typography';
import { A, navigate } from 'hookrouter';
import React from 'react';
import { connect } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { Field, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, InputAdornment, Stack, styled } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AppRoutes } from '../constants/AppRoutes';
import { AuthStatus } from '../constants/AuthStatus';
import { ForgotPasswordFormFields } from '../constants/FormFields';
import { Forms } from '../constants/Forms';
import { I18nKeys } from '../constants/I18nKeys';
import { required } from '../utils/reduxFormUtils';
import { InputField } from './redux-form/InputField';
import { i18n } from '../i18n';
import { setCurrentUserAuthStatus } from '../ducks/currentUserSlice';
import { SignInPage } from './SignInPage';
import { Form } from './redux-form/Form';
import { Button } from './library/Button';
import { useAppDispatch } from '../hooks';
import { AppState } from '../types/AppState';

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  alignSelf: 'center',
}));

interface FormData {
  [ForgotPasswordFormFields.Email]: string;
  [ForgotPasswordFormFields.VerificationCode]: string;
  [ForgotPasswordFormFields.NewPassword]: string;
  [ForgotPasswordFormFields.ConfirmNewPassword]: string;
}

interface StateProps {
  authStatus: AuthStatus;
}

interface DispatchProps {
  resendVerificationCode(): void;
  onSubmit(data: FormData): Promise<any>;
}

type Props = StateProps & DispatchProps & InjectedFormProps<FormData>;

const ForgotPasswordComponent: React.FC<Props> = ({
  authStatus,
  error,
  handleSubmit,
  resendVerificationCode,
  onSubmit,
  submitting,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownNewPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <SignInPage>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {authStatus !== AuthStatus.ForgotPasswordCodeSent && authStatus !== AuthStatus.PasswordReset && (
          <>
            <Typography component="p">{t(I18nKeys.ForgotPasswordMessage)}</Typography>

            <Field
              autoComplete="username"
              validate={required}
              name={ForgotPasswordFormFields.Email}
              component={InputField}
              label={`${t(I18nKeys.FieldEmail)}*`}
            />

            <Typography>
              {t(I18nKeys.SignInAlreadyHaveLoginMessage)}
              <A style={{ justifyItems: 'center', textDecoration: 'none' }} href={AppRoutes.SignIn}>
                <Typography component="span" color="#1B69B6">
                  {` ${t(I18nKeys.SignInButton)}`}
                </Typography>
              </A>
            </Typography>

            {!!error && <ErrorMessage>{error}</ErrorMessage>}
          </>
        )}

        {authStatus === AuthStatus.ForgotPasswordCodeSent && (
          <>
            <Typography component="p">{t(I18nKeys.ForgotPasswordResetMessage)}</Typography>

            <Field
              validate={required}
              name={ForgotPasswordFormFields.VerificationCode}
              component={InputField}
              label={`${t(I18nKeys.ForgotPasswordVerificationCodeInput)}*`}
            />

            <Field
              autoComplete="new-password"
              type={showNewPassword ? 'text' : 'password'}
              validate={required}
              name={ForgotPasswordFormFields.NewPassword}
              component={InputField}
              label={`${t(I18nKeys.ForgotPasswordNewPasswordInput)}*`}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownNewPassword}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack spacing="8px">
              <Field
                autoComplete="new-password"
                type={showConfirmPassword ? 'text' : 'password'}
                validate={required}
                name={ForgotPasswordFormFields.ConfirmNewPassword}
                component={InputField}
                label={`${t(I18nKeys.ForgotPasswordConfirmNewPasswordInput)}*`}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleClickShowConfirmPassword}
                          onMouseDown={handleMouseDownConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <A style={{ justifyItems: 'center', textDecoration: 'none' }} onClick={resendVerificationCode} href="">
                <Typography color="#1B69B6">{t(I18nKeys.ForgotPasswordResendVerificationLink)}</Typography>
              </A>

              {!!error && <ErrorMessage>{error}</ErrorMessage>}
            </Stack>
          </>
        )}

        {authStatus === AuthStatus.PasswordReset && (
          <Typography component="p">{t(I18nKeys.ForgotPasswordResetSuccessMessage)}</Typography>
        )}
      </Form>
      <Box>
        <Button
          variant="contained"
          loading={submitting}
          disabled={submitting}
          sx={{
            width: '262px',
            backgroundColor: '#F36F3A !important',
            '& .MuiButton-loadingIndicator': { backgroundColor: '#F36F3A !important' },
          }}
          onClick={() => {
            if (authStatus === AuthStatus.PasswordReset) {
              navigate(AppRoutes.SignIn);
              return;
            }
            dispatch(submit(Forms.ForgotPassword));
          }}
        >
          {(() => {
            switch (authStatus) {
              case AuthStatus.ForgotPasswordCodeSent:
                return t(I18nKeys.ForgotPasswordResetPasswordButton);
              case AuthStatus.PasswordReset:
                return t(I18nKeys.ForgotPasswordReturnToSignInButton);
              default:
                return t(I18nKeys.ForgotPasswordSendVerificationCodeButton);
            }
          })()}
        </Button>
      </Box>
    </SignInPage>
  );
};

const mapStateToProps = ({ currentUser: { authStatus } }: AppState): StateProps => ({ authStatus });

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  resendVerificationCode: (): AnyAction => dispatch(setCurrentUserAuthStatus(AuthStatus.SignedOut)),
  onSubmit: (values: FormData): Promise<AnyAction> =>
    new Promise((resolve, reject): void => {
      dispatch({
        type: `${Forms.ForgotPassword}_SUBMIT`,
        values,
        resolve,
        reject,
      });
    }),
});

const validate = ({
  [ForgotPasswordFormFields.NewPassword]: password,
  [ForgotPasswordFormFields.ConfirmNewPassword]: confirmPassword,
}: FormData): any => {
  if (password && confirmPassword && password !== confirmPassword) {
    return {
      [ForgotPasswordFormFields.ConfirmNewPassword]: `${i18n.t(I18nKeys.ForgotPasswordConfirmNewPasswordErrorMessage)}`,
    };
  }

  return {};
};

export const ForgotPassword = reduxForm<FormData>({ form: Forms.ForgotPassword, validate })(
  connect(mapStateToProps, mapDispatchToProps)(ForgotPasswordComponent),
);
